/* eslint-disable @typescript-eslint/no-magic-numbers */
import { FootPrintPadHoleType, isCopperLayer, matchLayerIdentifier, PcbLayoutFootprintPadShape, PcbNodeTypes, PcbViaType, } from "@buildwithflux/models";
import earcut from "earcut";
import { chunk } from "lodash";
import { Vector2 } from "three";
import { Clipper, EndType, JoinType } from "../clipper";
import { PcbNodeHelper } from "../nodes";
import { TraceCorners } from "../pcb/libs/traceCorners";
import { createShapeFromBakedRules } from "../shapes/createShapeFromBakedRules";
import { createCircleOrOblongShape } from "../shapes/primitives/createCircleOrOblongShape";
import { createRectangularShape } from "../shapes/primitives/createRectangularShape";
import { getStackupLayersAndViaConfigs, getViaSegmentFromType, isViaSegmentConnectingLayer } from "../smart_via";
import { calcKeepoutForNodes, canNodeHaveKeepout } from "./keepOutHelpers";
import { positionShapeRelativeToRootLayout } from "./positionShapeRelativeToRootLayout";
import { isThermalReliefRequired } from "./ThermalReliefs";
export var FillCutoutType;
(function (FillCutoutType) {
    /** Just fill it™️ */
    FillCutoutType[FillCutoutType["NoCutout"] = 0] = "NoCutout";
    /** For stuff that is supposed to be connected to the fill but is through hole, punch the hole only */
    FillCutoutType[FillCutoutType["HoleOnly"] = 1] = "HoleOnly";
    /** Keepout from hole, for pads/vias without an annular ring on that layer (governed by the connected layers rule) */
    FillCutoutType[FillCutoutType["HolePlusKeepout"] = 2] = "HolePlusKeepout";
    /** Keepout from outer shape, for pads/vias with an annular ring on that layer */
    FillCutoutType[FillCutoutType["OuterShapePlusKeepout"] = 3] = "OuterShapePlusKeepout";
})(FillCutoutType || (FillCutoutType = {}));
export function nodeHasFillOnLayer(node, layer) {
    const fillLayers = node?.bakedRules?.connectedLayers;
    return Boolean(isCopperLayer(layer) &&
        fillLayers &&
        fillLayers.some((layerId) => layerId === "All" || matchLayerIdentifier(layer, layerId)));
}
export function fillGetKeepoutMode(node, allNodes, layer, fillNetId) {
    // NOTE: this should never happen
    // NOTE: resetCache should be true when the active status of any node has changed
    if (!node.bakedRules) {
        return FillCutoutType.NoCutout;
    }
    if (!node.bakedRules.active) {
        return FillCutoutType.NoCutout;
    }
    const isOnLayer = matchLayerIdentifier(layer, node.bakedRules.layer);
    const isInNet = PcbNodeHelper.isCopperNodeType(node) && node.bakedRules.effectiveNetId === fillNetId;
    const isCopperDisplacing = PcbNodeHelper.isCopperNodeType(node) || node.type === PcbNodeTypes.zone;
    // Fill nodes are copper, but a fill doesn't avoid itself (and other fills on same layer is a more complex future feature)
    const isFill = node.type === PcbNodeTypes.fill;
    if (!isCopperDisplacing || isFill) {
        return FillCutoutType.NoCutout;
    }
    if (!isHoleNode(node) &&
        // Zones have multiple layers (like holes)
        node.type !== PcbNodeTypes.zone) {
        // Route segments and single-layer pad/vias, cut them out if they are not part of the fill net (no multi-layer issues)
        if (isInNet || !isOnLayer) {
            return FillCutoutType.NoCutout;
        }
        return FillCutoutType.OuterShapePlusKeepout;
    }
    // Should never happen, but we need to tell typescript that
    if (node.type === PcbNodeTypes.routeSegment) {
        throw new Error("Error in computing fill cutout mode: node is a route segment but has a hole");
    }
    // Through hole pad/vias need to follow the connectedLayers rule
    if (!node.bakedRules.connectedLayers) {
        return FillCutoutType.HolePlusKeepout;
    }
    // Through hole vias always have top and bottom
    // TODO: move this to a more sensible place in the codebase
    // TODO: make this work properly with smart vias canonical type
    const nodeConnLayers = [...node.bakedRules.connectedLayers];
    if (node.type === PcbNodeTypes.via && node.bakedRules.viaType === PcbViaType.throughHole) {
        nodeConnLayers.push("Top");
        nodeConnLayers.push("Bottom");
    }
    const hasPadOnLayer = nodeConnLayers.some((layerId) => layerId === "All" || matchLayerIdentifier(layer, layerId));
    // Zones don't behave like pads/vias: it they are not on a layer they are ignored
    if (!hasPadOnLayer && node.type === PcbNodeTypes.zone) {
        return FillCutoutType.NoCutout;
    }
    // Special case for alternate vias: we need to leave out the copper if the segment doesn't connect to the layer
    if (node.type === PcbNodeTypes.via && PcbNodeHelper.isNodeUnderSmartVia(node)) {
        const { copperLayers } = getStackupLayersAndViaConfigs(node.bakedRules, allNodes);
        const smartViaNode = allNodes[node.parentUid];
        const segmentInfo = getViaSegmentFromType(node.bakedRules.viaType, smartViaNode.bakedRules.selectedViaConfigs);
        const layerCopperOrder = copperLayers.findIndex(({ uid }) => layer.uid === uid);
        const segmentConnectsLayer = !!segmentInfo && isViaSegmentConnectingLayer(segmentInfo.segment, layerCopperOrder + 1);
        // If the via segment does not connect to the layer, we don't need to cut it out
        if (!segmentConnectsLayer) {
            return FillCutoutType.NoCutout;
        }
    }
    // If the layer is not connected to this pad/via, but we have a hole, we create a cutout whether or not the pad/via is in this net; the connectedLayers rule should always be respected.
    if (!hasPadOnLayer) {
        return FillCutoutType.HolePlusKeepout;
    }
    // If the layer has a pad but it's in the given fill net, just cut the hole
    if (isInNet) {
        return FillCutoutType.HoleOnly;
    }
    // If the layer has a pad but it's in another net, cut the outer shape + keepout
    return FillCutoutType.OuterShapePlusKeepout;
}
export function getThermalReliefCutoutShape(node, fillParentNode, divisions, context) {
    if (isThermalReliefRequired(node)) {
        const keepOut = getKeepoutForNodes(node, fillParentNode, context.pcbLayoutSelectorMatching);
        const keepOutShape = getCopperKeepOutZone(node, divisions, context, keepOut);
        return keepOutShape;
    }
    else {
        return undefined;
    }
}
export function getFillCutoutShape(node, fillParentNode, cutoutType, divisions, context, 
// TODO: used to get the copper shape without keepout, so that we can offset it later with a better value
// This is going to be removed soon when we enable precise keepouts for fills
noKeepout = false) {
    if (cutoutType === FillCutoutType.HoleOnly) {
        return getHoleShape(node, divisions);
    }
    if (cutoutType === FillCutoutType.OuterShapePlusKeepout) {
        const keepOut = noKeepout ? 0 : getKeepoutForNodes(node, fillParentNode, context.pcbLayoutSelectorMatching);
        return getCopperKeepOutZone(node, divisions, context, keepOut);
    }
    if (cutoutType === FillCutoutType.HolePlusKeepout) {
        const keepOut = noKeepout ? 0 : getKeepoutForNodes(node, fillParentNode, context.pcbLayoutSelectorMatching);
        const holeShape = getHoleShape(node, divisions);
        if (keepOut != 0) {
            return holeShape?.offset(keepOut, {
                joinType: JoinType.Miter,
            });
        }
        else {
            return holeShape;
        }
    }
    return undefined;
}
export function getKeepoutForNodes(nodeA, nodeB, pcbLayoutSelectorMatching) {
    return canNodeHaveKeepout(nodeA) && canNodeHaveKeepout(nodeB)
        ? calcKeepoutForNodes(nodeA, nodeB, pcbLayoutSelectorMatching)
        : 0;
}
/**
 * Return a node's keepout zone for copper fills.
 * Relative to layout space, not the node's space, because by the stage this function
 * returns, we're ready to combine the keepouts and cut them out of the copper.
 * TODO could also be useful to have an in between function that returns the keepout
 * shape relative to the node space.
 */
export function getCopperKeepOutZone(node, divisions = 32, context, keepOut) {
    if (!node.bakedRules) {
        // TODO would be good if we could ensure bakedRules exists fully intact in the node type
        // definition, well before this stage of rendering. Not just for copper fills but in general.
        return undefined;
    }
    const rules = node.bakedRules;
    // TODO: now we have a generic distance to every net, eventually we want to support precise spacing to specific nets
    const copperShape = getCopperShape(node, divisions, context);
    if (!copperShape) {
        return undefined;
    }
    const position = rules.positionRelativeToRootLayout;
    const rotation = rules.rotationRelativeToRootLayout;
    if (!position || !rotation) {
        // TODO This should really be defined by this point. It's not, a) because not all
        // documents actually have a Layout element, eg parts, and b) because of the general
        // case where bakedRules isn't yet defined. (b) is discussed above, but we should
        // more clearly define what scenario (a) means and how it should behave.
        return undefined;
    }
    const copperShapeTransformed = positionShapeRelativeToRootLayout(copperShape, position, rotation, rules.scale);
    // WATCH OUT! HACK!
    // If we don't do the offset here, the algorithm breaks
    // This is because copperShapeTransformed could be clockwise, while the rest of the algorithm expects counter-clockwise
    // Doing the .offset() fixes the winding order
    // BRYAN: it's possible you could still keep the perf optimisation in if you use ClipperShape.fixOrientation().
    const shapeWithKeepout = Clipper.shape([copperShapeTransformed]).offset(keepOut, {
        joinType: JoinType.Miter,
    });
    return shapeWithKeepout;
}
/**
 * Generates a cutout shape for the specified nodes. We intentionally take a simplified version of the shape, with 4
 * divisions by default, for performance as geometric quality is less an issue given that cutouts are used as obstacles
 * for island vias (positions for which are calculated by pole of inaccessibility which averages the distances from local
 * obstacles)
 *
 * Note; subtract controls the winding order of cutout shapes. Clockwise is additive, counter-clockwise is subtractive
 */
export function getPcbNodeCutOut(node, divisions = 4, context, subtract) {
    // TODO: consider caching here. we're likely re-generating same shapes from scratch, repeatedly, for pads, etc.
    const copperShape = getCopperShape(node, divisions, context);
    if (!copperShape) {
        return undefined;
    }
    const transformBakedRules = node.bakedRules;
    const position = transformBakedRules.positionRelativeToRootLayout;
    const rotation = transformBakedRules.rotationRelativeToRootLayout;
    if (!position || !rotation) {
        return undefined;
    }
    const polygon = positionShapeRelativeToRootLayout(copperShape, position, rotation, transformBakedRules.scale);
    const cutoutShape = Clipper.shape(subtract ? [polygon] : [polygon.reverse()]);
    return cutoutShape;
}
/**
 * Return the node's copper shape as a Vector2[]. Relative to the node itself, not
 * to world space, so that the shape definition doesn't change when node position changes.
 *
 * TODO this function must be pretty close to what the gerber exporter needs to produce;
 * let's make sure they ultimately share the same code where relevant.
 */
function getCopperShape(node, divisions = 32, context) {
    if (!node.bakedRules) {
        // TODO: revisit - this should be resolved now?
        // TODO per above, could we ensure bakedRules always exists by this stage?
        return undefined;
    }
    switch (node.type) {
        case PcbNodeTypes.pad:
        case PcbNodeTypes.zone: {
            const rules = node.bakedRules;
            // It's not really "copper shape" any more when it comes to holes; Is there a better name for this function?
            const size = node.type === PcbNodeTypes.pad && node.bakedRules.hole?.holeType === FootPrintPadHoleType.nonPlatedHole
                ? node.bakedRules.hole.holeSize
                : node.bakedRules.size;
            let copperShape;
            // the if then order matters! don't change it! we prioritize custom shapes over others
            const shape = node.type === PcbNodeTypes.pad ? node.bakedRules.padShape : node.bakedRules.zoneShape;
            const nodeHasShape = node.type !== PcbNodeTypes.pad || node.bakedRules.hole?.holeType !== FootPrintPadHoleType.nonPlatedHole;
            const shapeIsAsset = rules.asset ||
                (shape !== PcbLayoutFootprintPadShape.circular && shape !== PcbLayoutFootprintPadShape.rectangle);
            if (shapeIsAsset && nodeHasShape) {
                try {
                    // TODO: add logger
                    copperShape = createShapeFromBakedRules(rules, 0);
                }
                catch (error) {
                    // eslint-disable-next-line no-console
                    console.error(`Error loading shape: ${error}`);
                    return undefined;
                }
            }
            else if (shape === PcbLayoutFootprintPadShape.circular) {
                copperShape = createCircleOrOblongShape(size, { x: 0, y: 0 }, undefined, divisions);
            }
            else if (shape === PcbLayoutFootprintPadShape.rectangle) {
                copperShape = createRectangularShape(-size.x / 2, -size.y / 2, size.x, size.y, rules.cornerRadius);
                divisions /= 4; // Rounded corners need only a quarter of the number of vertices as circles
            }
            else {
                // Fallback to circle if unknown
                copperShape = createCircleOrOblongShape(size, { x: 0, y: 0 }, undefined, divisions);
                divisions /= 4;
            }
            return copperShape?.getPoints(divisions) ?? [];
        }
        case PcbNodeTypes.routeSegment: {
            try {
                const rules = node.bakedRules;
                const width = rules.size.x;
                const cornerStart = TraceCorners.computeForVertex("start", node, context.pcbNodes, context.pcbTracingGraph.getGraph());
                const cornerEnd = TraceCorners.computeForVertex("end", node, context.pcbNodes, context.pcbTracingGraph.getGraph());
                const shapes = [];
                for (const segment of TraceCorners.segmentGenerator(cornerStart?.arc)) {
                    const shape = Clipper.shape([[new Vector2(segment.start.x, segment.start.y), new Vector2(segment.end.x, segment.end.y)]], false).offset(width / 2, {
                        joinType: JoinType.Round,
                        endType: EndType.OpenRound,
                        arcTolerance: 1.5, // High arcTolerance (compared with default 0.25) because the high vertex count impacts other offset operations too.
                    });
                    shapes.push(shape);
                }
                for (const segment of TraceCorners.segmentGenerator(cornerEnd?.arc)) {
                    const shape = Clipper.shape([[new Vector2(segment.start.x, segment.start.y), new Vector2(segment.end.x, segment.end.y)]], false).offset(width / 2, {
                        joinType: JoinType.Round,
                        endType: EndType.OpenRound,
                        arcTolerance: 1.5, // High arcTolerance (compared with default 0.25) because the high vertex count impacts other offset operations too.
                    });
                    shapes.push(shape);
                }
                // Use ClipperJS to outset the infinitely-thin line to give it a stroke width:
                const shape = Clipper.shape([
                    [
                        new Vector2(cornerStart?.vertex.x ?? rules.startPosition.x, cornerStart?.vertex.y ?? rules.startPosition.y),
                        new Vector2(cornerEnd?.vertex.x ?? rules.endPosition.x, cornerEnd?.vertex.y ?? rules.endPosition.y),
                    ],
                ], false).offset(width / 2, {
                    joinType: JoinType.Round,
                    endType: EndType.OpenRound,
                    arcTolerance: 1.5, // High arcTolerance (compared with default 0.25) because the high vertex count impacts other offset operations too.
                });
                shapes.push(shape);
                // TODO: fix this:
                return Clipper.shape([]).union(shapes).toPolygon(0);
            }
            catch {
                return undefined;
            }
        }
        case PcbNodeTypes.via: {
            const rules = node.bakedRules;
            const copperShape = createCircleOrOblongShape(rules.size, { x: 0, y: 0 }, undefined, divisions);
            return copperShape.getPoints();
        }
        case PcbNodeTypes.fill: {
            // This block is here for completeness, but since this function is (currently)
            // only used for calculating the fill shape, it doesn't need to return the
            // fill shape itself. (In future, it should return the (pre-cached) fill shape,
            // eg possibly for gerber export. But make sure it's not circular recursion, prob
            // just by excluding fill node from the fill calculation higher up)
            return undefined;
        }
        default: {
            // Only copper node types have copper shapes.
            return undefined;
        }
    }
}
function isHoleNode(node) {
    if (node.type === PcbNodeTypes.pad &&
        (node.bakedRules?.hole?.holeType === FootPrintPadHoleType.nonPlatedHole ||
            node.bakedRules?.hole?.holeType === FootPrintPadHoleType.platedThroughHole)) {
        return true;
    }
    else if (node.type === PcbNodeTypes.via) {
        // Note: this will consider micro/blind/other vias to be always hole nodes. We should think through whether that still makes sense in future when we implement micro/blind vias.
        return true;
    }
    return false;
}
function getHoleShape(node, divisions = 64) {
    if (!node.bakedRules) {
        return undefined;
    }
    let shape;
    if (node.type === PcbNodeTypes.pad) {
        const rules = node.bakedRules;
        if (rules.hole) {
            shape = createCircleOrOblongShape(rules.hole.holeSize, { x: 0, y: 0 }, undefined, divisions);
        }
    }
    else if (node.type === PcbNodeTypes.via) {
        const rules = node.bakedRules;
        shape = createCircleOrOblongShape(rules.holeSize, { x: 0, y: 0 }, undefined, divisions);
    }
    if (shape) {
        let points = shape.getPoints(divisions);
        const rules = node.bakedRules;
        if (!rules.positionRelativeToRootLayout || !rules.rotationRelativeToRootLayout) {
            return undefined;
        }
        points = positionShapeRelativeToRootLayout(points, rules.positionRelativeToRootLayout, rules.rotationRelativeToRootLayout, rules.scale);
        const clipperShape = Clipper.shape([points]);
        // createCircleOrOblongShape returns polygons with reverse winding which messes
        // up unionMultiple's core polygon fill rule (important for perf). So fix it:
        clipperShape.fixOrientation();
        return clipperShape;
    }
    return undefined;
}
export function createPolygonShape(polygon, params) {
    try {
        const shape = createShapeFromBakedRules(polygon.bakedRules, 0);
        // TODO: great comments below, review this createShapeFromBakedRules(..) behavior - arguably an empty
        // svg result should return undefined to simplify the function's contract
        if (shape && !shape.curves.length) {
            // If the shape is empty, it likely means the SVG path couldn't be parsed.
            // Return undefined instead of an invalid empty layout shape (which calling
            // code would break on.)
            return undefined;
        }
        return shape;
    }
    catch (error) {
        if (params?.throwException) {
            throw error;
        }
        else {
            return undefined;
        }
    }
}
export function triangulateShape(shape) {
    if (shape.hasMultipleRegions()) {
        throw new Error("Cannot triangulate shape consisting multiple non-hole regions. Separate shapes first.");
    }
    const [poly, ...holes] = shape.toPolygons({ outlinesFirst: true });
    if (!poly) {
        return [];
    }
    // Note: in my partitioning tests, earcut triangulation is faster than poly-decomp:quickDecomp,
    // and moreover it can handle holes, whereas quickDecomp cannot. And we get almost zero
    // advantage if we can't handle holes because then we're forced to test full shape intersection
    // (with the complex island shape) in most cases that we would anyway, which is the whole thing
    // this optimises!
    //
    // Note 2: both earcut and quickDecomp are WAY faster than poly-partion
    //
    // Note 3: flattening the holes in a polygon (as opposed to ignoring them), eg with removePolyHoles,
    // or poly-partition:removeHoles, is extremely slow, so prefer earcut here for that reason too.
    const polyWithHoles = poly.flatMap((p) => [p.x, p.y]);
    const holeIndices = [];
    for (const hole of holes) {
        holeIndices.push(polyWithHoles.length / 2);
        polyWithHoles.push(...hole.flatMap((p) => [p.x, p.y]));
    }
    const triangles = earcut(polyWithHoles, holeIndices);
    const partitions = chunk(triangles, 3).map((tri) => tri.map((idx) => new Vector2(polyWithHoles[idx * 2], polyWithHoles[idx * 2 + 1])));
    return partitions;
}
//# sourceMappingURL=FillHelpers.js.map